import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Printer, Mail, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { COMPANY } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { PageHero, Eyebrow, useSeo } from "../components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Fulfillment & Warehousing",
  "Promotional Products & Apparel",
  "Trade Show Exhibits",
  "Event & Project Management",
  "Marketing Collateral",
  "Printing Services",
  "Other",
];

const field = "w-full border border-brand-line bg-white px-4 py-3 text-sm text-brand-black outline-none transition-colors placeholder:text-brand-gray/60 focus:border-brand-red";
const labelCls = "block text-xs font-bold uppercase tracking-wide text-brand-charcoal";

export default function Contact() {
  useSeo(
    "Contact MJG Promotions | Fairfield, NJ",
    "Contact MJG Promotions in Fairfield, NJ for fulfillment, promotional products, apparel, trade show exhibits, marketing collateral, and event support. Call 973-575-8877."
  );

  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", email: "", phone: "",
    service: "", message: "", need_by: "", quantity: "", consent: false, website: "",
  });
  const [attachmentName, setAttachmentName] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const validate = () => {
    const err = {};
    ["first_name", "last_name", "company", "email", "phone", "service", "message"].forEach((k) => {
      if (!form[k] || !String(form[k]).trim()) err[k] = "Required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    if (!form.consent) err.consent = "Consent is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setAttachmentName(""); return; }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large", { description: "Please keep uploads under 10MB." });
      e.target.value = "";
      return;
    }
    setAttachmentName(f.name);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, attachment_name: attachmentName || null });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or call us at " + COMPANY.phone });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's Put Your Brand in Motion"
        intro="Tell us what you need to produce, store, fulfill, or bring to your next event. Our team will help you identify the right next step."
      />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            {/* Contact details */}
            <div className="lg:col-span-4">
              <Eyebrow>Reach Us</Eyebrow>
              <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-black">Talk to Our Team</h2>
              <div className="mt-8 space-y-6 text-sm">
                <a href={COMPANY.phoneHref} data-testid="contact-phone" className="flex items-start gap-3 text-brand-charcoal hover:text-brand-red">
                  <Phone className="mt-0.5 h-5 w-5 text-brand-red" aria-hidden="true" />
                  <span><span className="block font-bold">Phone</span>{COMPANY.phone}</span>
                </a>
                <div className="flex items-start gap-3 text-brand-charcoal">
                  <Printer className="mt-0.5 h-5 w-5 text-brand-red" aria-hidden="true" />
                  <span><span className="block font-bold">Fax</span>{COMPANY.fax}</span>
                </div>
                <a href={COMPANY.emailHref} data-testid="contact-email" className="flex items-start gap-3 text-brand-charcoal hover:text-brand-red">
                  <Mail className="mt-0.5 h-5 w-5 text-brand-red" aria-hidden="true" />
                  <span><span className="block font-bold">Email</span>{COMPANY.email}</span>
                </a>
                <div className="flex items-start gap-3 text-brand-charcoal">
                  <MapPin className="mt-0.5 h-5 w-5 text-brand-red" aria-hidden="true" />
                  <span><span className="block font-bold">Address</span>{COMPANY.addressLine1}<br />{COMPANY.addressLine2}</span>
                </div>
              </div>
              {/* CLIENT-TODO: embed map after confirming location + consent/privacy implementation. */}
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              {done ? (
                <Reveal>
                  <div className="flex flex-col items-start rounded-sm border border-brand-line bg-brand-warm p-10" data-testid="contact-success">
                    <CheckCircle2 className="h-12 w-12 text-brand-red" aria-hidden="true" />
                    <h3 className="mt-5 text-2xl font-extrabold text-brand-black">Thank you — your request is in.</h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-brand-gray">
                      Our team will review your details and follow up shortly. If it's urgent, call us at{" "}
                      <a href={COMPANY.phoneHref} className="font-semibold text-brand-red">{COMPANY.phone}</a>.
                    </p>
                  </div>
                </Reveal>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="grid gap-5 sm:grid-cols-2">
                  {/* honeypot */}
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />

                  <div>
                    <label htmlFor="first_name" className={labelCls}>First name *</label>
                    <input id="first_name" data-testid="input-first-name" className={`${field} mt-2`} value={form.first_name} onChange={set("first_name")} />
                    {errors.first_name && <p className="mt-1 text-xs text-brand-red">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="last_name" className={labelCls}>Last name *</label>
                    <input id="last_name" data-testid="input-last-name" className={`${field} mt-2`} value={form.last_name} onChange={set("last_name")} />
                    {errors.last_name && <p className="mt-1 text-xs text-brand-red">{errors.last_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="company" className={labelCls}>Company *</label>
                    <input id="company" data-testid="input-company" className={`${field} mt-2`} value={form.company} onChange={set("company")} />
                    {errors.company && <p className="mt-1 text-xs text-brand-red">{errors.company}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className={labelCls}>Work email *</label>
                    <input id="email" type="email" data-testid="input-email" className={`${field} mt-2`} value={form.email} onChange={set("email")} />
                    {errors.email && <p className="mt-1 text-xs text-brand-red">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className={labelCls}>Phone *</label>
                    <input id="phone" data-testid="input-phone" className={`${field} mt-2`} value={form.phone} onChange={set("phone")} />
                    {errors.phone && <p className="mt-1 text-xs text-brand-red">{errors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="service" className={labelCls}>Service needed *</label>
                    <select id="service" data-testid="input-service" className={`${field} mt-2`} value={form.service} onChange={set("service")}>
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.service && <p className="mt-1 text-xs text-brand-red">{errors.service}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className={labelCls}>Project or program description *</label>
                    <textarea id="message" rows={5} data-testid="input-message" className={`${field} mt-2 resize-y`} value={form.message} onChange={set("message")} />
                    {errors.message && <p className="mt-1 text-xs text-brand-red">{errors.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="need_by" className={labelCls}>Need-by date <span className="font-normal normal-case text-brand-gray">(optional)</span></label>
                    <input id="need_by" type="date" data-testid="input-need-by" className={`${field} mt-2`} value={form.need_by} onChange={set("need_by")} />
                  </div>
                  <div>
                    <label htmlFor="quantity" className={labelCls}>Est. quantity / # of events <span className="font-normal normal-case text-brand-gray">(optional)</span></label>
                    <input id="quantity" data-testid="input-quantity" className={`${field} mt-2`} value={form.quantity} onChange={set("quantity")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="file" className={labelCls}>Attach a file <span className="font-normal normal-case text-brand-gray">(optional — PDF, DOC, PNG, JPG up to 10MB)</span></label>
                    <input id="file" type="file" data-testid="input-file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xls,.xlsx" onChange={onFile} className={`${field} mt-2 file:mr-3 file:border-0 file:bg-brand-black file:px-3 file:py-1 file:text-white`} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 text-sm text-brand-charcoal">
                      <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-[#C8102E]" />
                      <span>I agree to MJG Promotions contacting me about my request and consent to the <span className="font-semibold text-brand-red">Privacy Policy</span>. *</span>
                    </label>
                    {errors.consent && <p className="mt-1 text-xs text-brand-red">{errors.consent}</p>}
                  </div>

                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="group inline-flex items-center gap-2 bg-brand-red px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-redhover disabled:opacity-60"
                    >
                      {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Request a Consultation"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
